import React, { useCallback, useContext, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, List } from 'antd';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { User } from '@totem/components/users/container/types';
import { debounce } from '@totem/utilities/debounce';
import { API_V1 } from '@totem/utilities/endpoints';

const { Search } = Input;

const DEBOUNCE_TIME = 500;

type Props = {
  existingEmails: string[];
  onAdd: (email: string) => void;
};

const AssigneeSearchInput = ({ existingEmails, onAdd }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(
    (term: string) => {
      if (!term) {
        setResults([]);
        return;
      }
      setLoading(true);

      fetch(`${API_V1}/internalUsers/search`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify({ page: 0, pageSize: 20, searchTerm: term }),
      })
        .then((res) => res.json())
        .then((result: User[]) => {
          setResults(Array.isArray(result) ? result : []);
        })
        .finally(() => setLoading(false));
    },
    [getApiToken],
  );

  const sendChange = useCallback(debounce(runSearch, DEBOUNCE_TIME), [
    runSearch,
  ]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);

    sendChange(value);
  };

  const activeEmails = existingEmails.map((email) => email.toLowerCase());

  return (
    <>
      <Search
        placeholder="Search users by name or email"
        value={searchTerm}
        onSearch={handleSearch}
        onChange={(evt) => handleSearch(evt.target.value)}
        loading={loading}
      />
      {searchTerm && (
        <List
          size="small"
          dataSource={results}
          locale={{ emptyText: 'No matching users' }}
          renderItem={(user) => {
            const isActive = activeEmails.includes(user.email.toLowerCase());
            return (
              <List.Item
                actions={[
                  <Button
                    key="add"
                    type="link"
                    icon={<PlusOutlined />}
                    disabled={isActive}
                    onClick={() => onAdd(user.email)}
                  >
                    {isActive ? 'Already Assigned' : 'Add'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={user.email}
                  description={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                />
              </List.Item>
            );
          }}
        />
      )}
    </>
  );
};

export default AssigneeSearchInput;
